import {Button, Card, Col, Form, notification, Row, Tabs} from "antd";
import DatePicker from "../../core/components/DatePicker";
import LayoutWithSider from "../../core/components/layout/layout-with-sider/LayoutWithSider";
import {DataGridProvider} from "../../core/components/data-grid/DataGridContext";
import HospitalDashboard from "../../app/dashboard/HospitalDashboard";
import ReadUserDashboard from "../../app/dashboard/ReadUserDashboard";
import WrittenUserDashboard from "../../app/dashboard/WrittenUserDashboard";
import MyDashboard from "../../app/dashboard/panels/MyDashboard";
import {adminTabItems} from "../../app/dashboard/panels/AdminPanel";
import {InfoTitle} from "../../app/dashboard/panels/PanelParts";
import {info} from "../../app/dashboard/panels/info";
import QuickDatePicker from "../../core/components/data-grid/QuickDatePicker";
import {useEffect, useRef, useState} from "react";
import {DATE_OPTIONS, getBeginStr, getDateFromNow, getEndStr} from "../../core/utils/date-utils";
import {t} from 'i18next'
import {useApp} from "../../core/context/AppContext";
import ModalitySelectBox from "../../app/modality/ModalitySelectBox";
import UserSelectBox from "../../app/user/UserSelectBox";
import {PERMISSIONS, useUser} from "../../core/context/UserContext";
import ApiService from "../../core/service/api-service";
import {downloadExcel, DownloadTableExcel} from "react-export-table-to-excel";
import FileUtils from "../../core/utils/file-utils";

export default function DashboardPage() {
    const formRef = useRef()
    const {modalities} = useApp()
    const {hasPermission, showDashboard, config} = useUser()

    // Teleradyoloji pano sekmeleri sistem ayarıyla (App.EnableTeleradiologyDashboard) açılır; kapalıyken
    // yalnız İstatistikler kalır. Yönetici sekmeleri TELERADIOLOGY_DASHBOARD izniyle (ayar kapalıyken
    // hasPermission zaten false döner); İstatistikler sekmesi eskisi gibi DASHBOARD ile.
    // Doktor = ses kaydı ekleyebilen, raportör = rapor yazabilen (okuyan/yazan kullanıcı listeleriyle aynı ayrım).
    const panelsEnabled = !!config?.enableTeleradiologyDashboard
    const isDoctor = hasPermission(PERMISSIONS.VOICE_RECORD_ADD)
    const isReporter = hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT)
    const isManager = hasPermission(PERMISSIONS.TELERADIOLOGY_DASHBOARD)
    const [dashboard, setDashboard] = useState({
        hospitalData: [],
        reporterData: [],
        doctorData: []
    })
    const hospitalTableRef = useRef()
    const reporterTableRef = useRef()
    const doctorTableRef = useRef()

    const date = getDateFromNow(DATE_OPTIONS.THIS_MONTH)

    const onFinish = async () => {
        const form = formRef.current.getFieldsValue();
        const filter = {
            ...form,
            reportCreatedDateBegin: getBeginStr(form.reportCreatedDateBegin),
            reportCreatedDateEnd: getEndStr(form.reportCreatedDateEnd),
        }

        const _dashboard = {}
        const resHospital = await ApiService.Dashboard.HospitalStatistics(filter)
        if (resHospital.success) {
            _dashboard.hospitalData = resHospital.data
        }

        const resDoctor = await ApiService.Dashboard.ReadUserStatistics(filter)
        if (resDoctor.success) {
            _dashboard.doctorData = resDoctor.data
        }

        const resReporter = await ApiService.Dashboard.WrittenUserStatistics(filter)
        if (resReporter.success) {
            _dashboard.reporterData = resReporter.data
        }

        setDashboard(_dashboard)
    }

    // antd v5'te <Table ref> bir DOM düğümü DEĞİL, {nativeElement, scrollTo} nesnesidir
    // (bkz. antd/es/table/InternalTable.js). Bu yüzden ref.current.cloneNode() TypeError
    // atıyor ve Excel butonu hiçbir şey yapmıyordu. Gerçek <table> düğümünü buradan çözüyoruz;
    // yetkisi olmayan kullanıcıda ilgili tablo hiç basılmadığı için null dönebilir.
    const getTableNode = (ref) => {
        const root = ref.current?.nativeElement || ref.current;
        if (!root || typeof root.querySelector !== 'function')
            return null;
        return root.querySelector('table') || root;
    };

    const toExcel = (table, name, fileName) => {
        const template =
            '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--><meta http-equiv="content-type" content="text/plain; charset=UTF-8"/></head><body><table>{table}</table></body></html>';

        // Yerine koyma fonksiyon biçiminde: tablo içeriğindeki '$&' gibi diziler
        // String.replace'in özel değişkenleri olarak yorumlanmasın.
        const html = template
            .replace('{worksheet}', () => name || 'Worksheet')
            .replace('{table}', () => table.innerHTML);

        // Eskiden data: URI kullanılıyordu; Chrome üst çerçevenin data: URL'e gitmesini
        // engelliyor, ayrıca büyük tablolarda URI uzunluk sınırına takılıyordu.
        // Blob + geçici <a download> her tarayıcıda çalışıyor (link DOM'a eklenmeli:
        // Firefox bağlı olmayan bir <a>'nın click'ini yok sayıyor).
        // Başa BOM: Excel dosyayı UTF-8 olarak açsın, Türkçe karakterler bozulmasın.
        const blob = new Blob(['\ufeff', html], {type: 'application/vnd.ms-excel;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <LayoutWithSider fullWidth={true}>
                <Tabs defaultActiveKey="statistics" items={[
                    {
                        key: 'statistics',
                        label: t('Statistics'),
                        children: (
                            <Row justify={'center'} gutter={[24, 12]}>
                                <Col xs={24}>
                                    <Card>
                                        <Form
                                            size={'small'}
                                            ref={formRef}
                                            initialValues={{
                                                reportCreatedDateBegin: date.start,
                                                reportCreatedDateEnd: date.end
                                            }}
                                            onFinish={onFinish}
                                        >
                                            <div
                                                style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8}}
                                            >
                                                <Form.Item
                                                    style={{marginBottom: 0}}
                                                >
                                                    <QuickDatePicker
                                                        onChange={(start, end) => {
                                                            formRef.current.setFieldsValue({
                                                                reportCreatedDateBegin: start,
                                                                reportCreatedDateEnd: end,
                                                            })
                                                        }}
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    style={{marginBottom: 0}}
                                                    label={t('')}
                                                    name="reportCreatedDateBegin"
                                                >
                                                    <DatePicker showToday={false}/>
                                                </Form.Item>

                                                <Form.Item
                                                    style={{marginBottom: 0}}
                                                    label={''}
                                                    name="reportCreatedDateEnd"
                                                >
                                                    <DatePicker showToday={false}/>
                                                </Form.Item>

                                                {/* Kullanıcı süzgeci yalnız tüm kullanıcıları görebilenlere gösterilir;
                                                    yetkisi olmayan zaten backend'de kendi satırlarına kilitli. */}
                                                {showDashboard('admin') && (
                                                    <>
                                                        <Form.Item
                                                            style={{marginBottom: 0, minWidth: 200}}
                                                            name="studyReadUserIdList"
                                                        >
                                                            <UserSelectBox
                                                                type={'read'}
                                                                mode={'multiple'}
                                                                maxTagCount={'responsive'}
                                                                placeholder={t('Doctor')}
                                                            />
                                                        </Form.Item>

                                                        <Form.Item
                                                            style={{marginBottom: 0, minWidth: 200}}
                                                            name="reportCreatedUserIdList"
                                                        >
                                                            <UserSelectBox
                                                                type={'write'}
                                                                mode={'multiple'}
                                                                maxTagCount={'responsive'}
                                                                placeholder={t('Reporter')}
                                                            />
                                                        </Form.Item>
                                                    </>
                                                )}

                                                <Form.Item
                                                    style={{marginBottom: 0}}
                                                >
                                                    <Button type={'primary'} htmlType={'submit'}>
                                                        Search
                                                    </Button>
                                                </Form.Item>

                                                <Button size={'small'} color={'green'} variant={'solid'} onClick={() => {
                                                    const start = formRef.current.getFieldsValue().reportCreatedDateBegin;
                                                    const end = formRef.current.getFieldsValue().reportCreatedDateEnd;

                                                    // Yetkiye göre üç tablodan bazıları hiç basılmamış olabilir;
                                                    // eksik olanı atla, hepsi eksikse kullanıcıya söyle.
                                                    const nodes = [
                                                        getTableNode(hospitalTableRef),
                                                        getTableNode(doctorTableRef),
                                                        getTableNode(reporterTableRef),
                                                    ].filter(Boolean)

                                                    if (!nodes.length) {
                                                        notification.warning({message: t('dashboardExcelNoTable')})
                                                        return
                                                    }

                                                    const table = document.createElement('table')
                                                    const rowHeader = document.createElement('tr')
                                                    rowHeader.innerHTML = `
                                                    <td style="font-size: 13pt"><span style="font-weight: bold">Tarih:</span> ${new Date(
                                                        start
                                                    ).toLocaleString()} - ${new Date(
                                                        end
                                                    ).toLocaleString()}</td>
                                                    `;

                                                    table.appendChild(rowHeader)
                                                    nodes.forEach(node => {
                                                        const row = document.createElement('tr')
                                                        const cell = document.createElement('td')
                                                        cell.appendChild(node.cloneNode(true))
                                                        row.appendChild(cell)
                                                        table.appendChild(row)
                                                    })

                                                    const fileName = `Dashboard_${new Date(
                                                        start
                                                    ).toLocaleDateString()}_${new Date(
                                                        end
                                                    ).toLocaleDateString()}.xls`;
                                                    toExcel(table, 'Dashboard', fileName);
                                                }}
                                                >
                                                    Excel
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card>
                                </Col>

                                {
                                    showDashboard() && (
                                        <Col xs={24}>
                                            <Card title={<InfoTitle title={t('Hospital')} info={info('statsHospital')}/>}>
                                                <HospitalDashboard
                                                    tableRef={hospitalTableRef}
                                                    modalities={modalities}
                                                    data={dashboard.hospitalData}
                                                />
                                            </Card>
                                        </Col>
                                    )
                                }
                                {
                                    showDashboard('doctor') && (
                                        <Col xs={24}>
                                            <Card title={<InfoTitle title={t('Doctor')} info={info('statsDoctor')}/>}>
                                                <ReadUserDashboard
                                                    tableRef={doctorTableRef}
                                                    modalities={modalities}
                                                    data={dashboard.doctorData}/>
                                            </Card>
                                        </Col>
                                    )
                                }
                                {
                                    showDashboard('reporter') && (
                                        <Col xs={24}>
                                            <Card title={<InfoTitle title={t('Reporter')} info={info('statsReporter')}/>}>
                                                <WrittenUserDashboard
                                                    tableRef={reporterTableRef}
                                                    modalities={modalities}
                                                    data={dashboard.reporterData}/>
                                            </Card>
                                        </Col>
                                    )
                                }
                            </Row>
                        )
                    },
                    panelsEnabled && (isDoctor || isReporter || isManager) && {
                        key: 'my',
                        label: t('My dashboard'),
                        children: <MyDashboard isDoctor={isDoctor} isReporter={isReporter} canPickUser={isManager}/>
                    },
                    ...(panelsEnabled && isManager ? adminTabItems() : []),
                ].filter(Boolean)} />
            </LayoutWithSider>
        </>
    )
}
